/**
 * Replay: baut eine Partie allein aus Seed und Zugprotokoll wieder auf.
 * Weil die Engine ohne Math.random auskommt, ist das Ergebnis bitgenau
 * reproduzierbar. Darauf stützen sich die serverseitige Prüfung von
 * Highscores (Edge Function `verify-run`) und die Mehrspieler-Synchronisation.
 */
import { actions, journal, dispatchAs, type Action } from './actions';
import { fresh, setS, S } from './state';
import { RULES } from './version';
import type { GameState } from './types';

export const MAX_JOURNAL = 20000;

/** Regelversion aus dem Zugprotokoll: zweites Argument von `newGame`, sonst 1 (Partien von vor der Versionierung). */
export function versionOf(moves: Action[]): number {
  const ng = moves.find((a) => a && a.type === 'newGame');
  const v = Number(ng?.args?.[1]);
  return Number.isInteger(v) && v > 0 ? v : 1;
}

export function isAction(a: unknown): a is Action {
  return !!a && typeof a === 'object' && typeof (a as Action).type === 'string' && (a as Action).type in actions && Array.isArray((a as Action).args);
}

/**
 * Spielt `moves` auf eine frische Partie mit `seed` an. Gibt den Endzustand zurück.
 * Der in-memory-Journal wird dabei ersetzt, damit danach normal weitergespielt werden kann.
 */
export function replay(seed: number, moves: Action[], ver?: number): GameState {
  if (moves.length > MAX_JOURNAL) throw new Error('Zugprotokoll zu lang');
  const v = ver ?? versionOf(moves);
  if (v > RULES) throw new Error(`Partie nach Regelversion ${v}, dieser Code kennt nur bis ${RULES}`);
  setS(fresh(seed, v));
  journal.length = 0;
  for (const a of moves) {
    if (!isAction(a)) throw new Error('Ungültiger Zug: ' + JSON.stringify(a).slice(0, 80));
    if (a.type === 'newGame') continue; // der Seed steht schon fest
    dispatchAs(a.as === 'guest' ? 'guest' : 'host', a.type, a.args);
  }
  return S;
}

/** Kurzer Fingerabdruck eines Zustands, um zwei Replays zu vergleichen. */
export function fingerprint(s: GameState): string {
  const parts = [s.phase, s.m, s.leg, s.laws.length, Math.round(s.saldo * 100), Math.round(s.debt * 100), s.career.length, s.rng?.draws ?? 0];
  return parts.join(':');
}
